import { Button } from '@econsult/econsult-ui/dist/components/Button';
import Stack from '@mui/material/Stack';
import { activateDayPass, activateMonthPass } from 'store/action/wallet';
import { useAppDispatch } from 'store/configureStore';
import { Passes } from 'store/reducer/wallet';
import styled from 'styled-components';
import { ParagraphSmallCapsHeavy, ParagraphSmallRegular } from 'styles/elements/typography';

const BalanceContainer = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 16px;
  gap: 16px;
`;

const BalanceSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const Label = styled(ParagraphSmallCapsHeavy)`
  && {
    color: ${({ theme }) => theme.colorNeutralNeutral1};
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
    font-weight: 600;
  }
`;

const BalanceValue = styled(ParagraphSmallRegular)`
  && {
    color: ${({ theme }) => theme.colorPrimaryPrimary70};
  }
`;

const formatSeconds = (seconds: number) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
};

interface BalanceProps {
  freeBalanceSeconds: number;
  paidBalanceSeconds: number;
  totalUsableSeconds: number;
  passes: Passes | null;
  selectedPracticeId: string | undefined;
}

export default function Balance({
  freeBalanceSeconds,
  paidBalanceSeconds,
  totalUsableSeconds,
  passes,
  selectedPracticeId,
}: BalanceProps) {
  const dispatch = useAppDispatch();
  const hasActivePass = Boolean(passes?.activePass);
  const remainingPasses = passes?.remainingPasses ?? [];
  const dayPasses = remainingPasses.filter(pass => pass.label.toLowerCase().includes('day'));
  const monthPasses = remainingPasses.filter(pass => pass.label.toLowerCase().includes('month'));
  const handleActivateDayPass = () => {
    if (selectedPracticeId) {
      activateDayPass(dispatch, dayPasses, selectedPracticeId);
    }
  };
  const handleActivateMonthPass = () => {
    if (selectedPracticeId) {
      activateMonthPass(dispatch, monthPasses, selectedPracticeId);
    }
  };
  const noDayPassesText = 'No Day Passes available';
  const noMonthPassesText = 'No Month Passes available';
  const hasActivePassText = 'Pass already active';
  const dayPassText = hasActivePass ? hasActivePassText : `Activate Day Pass 1/${dayPasses.length}`;
  const monthPassText = hasActivePass ? hasActivePassText : `Activate Month Pass 1/${monthPasses.length}`;
  return (
    <BalanceContainer>
      <BalanceSection>
        <Label>Free Balance</Label>
        <BalanceValue>{formatSeconds(freeBalanceSeconds)}</BalanceValue>
      </BalanceSection>
      <BalanceSection>
        <Label>Paid Balance</Label>
        <BalanceValue>{formatSeconds(paidBalanceSeconds)}</BalanceValue>
      </BalanceSection>
      <Stack sx={{ gap: 1 }}>
        {[
          {
            type: 'day',
            passes: dayPasses,
            handler: handleActivateDayPass,
            noText: noDayPassesText,
            passText: dayPassText,
            id: 'ActiveDayPassButton',
          },
          {
            type: 'month',
            passes: monthPasses,
            handler: handleActivateMonthPass,
            noText: noMonthPassesText,
            passText: monthPassText,
            id: 'ActiveMonthPassButton',
          },
        ].map(({ type, passes, handler, noText, passText, id }) => (
          <Button
            key={type}
            size="small"
            state="primary"
            id={id}
            onClick={handler}
            disabled={hasActivePass || passes.length === 0}
            data-automation="AskCopilotButton"
            style={{ padding: '0 8px', fontSize: '14px', height: '28px' }}
          >
            {passes.length ? passText : noText}
          </Button>
        ))}
      </Stack>
      <BalanceSection>
        <Label>Total Usable</Label>
        <BalanceValue>{formatSeconds(totalUsableSeconds)}</BalanceValue>
      </BalanceSection>
    </BalanceContainer>
  );
}
